/**
 * General number formatting utilities for UI display.
 *
 * Use formatNumber() for:
 * - Tables, forms, counts, and general numeric display
 * - Values that need comma separators (e.g., "1,234.56")
 * - Currency formatting (e.g., "$1,234.56")
 * - Appending static units (e.g., "1,234 acres")
 *
 * Use formatFixed() for:
 * - Fixed precision with commas (e.g., formatFixed(value, 8) -> "1,234.56000000")
 *
 * Use format0/format1/format2/format3/format8/format10 presets for common precisions:
 * - format0(value) for integers/counts (0 decimals)
 * - format1(value) for elevations, coordinates (1 decimal)
 * - format2(value) for percentages, currency (2 decimals)
 * - format3(value) for volumes (3 decimals)
 * - format8(value) for acres, ownership values (8 decimals)
 * - format10(value) for interest values (10 decimals)
 *
 * Use withPrecision() to create custom precision formatters:
 * - const format4 = withPrecision(4);
 *
 * Use formatRange() for:
 * - Numeric ranges (e.g., "1,000 - 2,000")
 *
 * Use formatCountWithPercent() for:
 * - Counts with percentages (e.g., "100 (26.24%)")
 *
 * For Highcharts axis labels and tooltips with scale suffixes (K, MM, B, T),
 * use formatScaledNumber() from components/free-cash-flow/numberFormatter.js
 */
import { isNil, isPlainObject } from 'lodash-es';

type NumericValue = number | string | undefined | null;

interface FormatNumberOptions {
  value: NumericValue;
  minPrecision: number;
  maxPrecision?: number;
  style?: 'currency' | 'decimal';
  includeCommas?: boolean;
  fallback?: string;
  unit?: string;
}

// Parses a numeric value that may be a number, string, or money-formatted string
// Examples: 1234.56, "1234.56", "$1,234.56", "($1,234.56)" (negative)
function parseNumericValue(value: NumericValue): number | null {
  if (isNil(value)) return null;
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  // Remove currency symbols, commas, and whitespace
  // Handle negative values in parentheses: ($1,234.56) -> -1234.56
  const isNegative = value.includes('(') && value.includes(')');
  const cleaned = value.replace(/[$,()]/g, '').trim();

  if (cleaned === '') return null;

  const parsed = parseFloat(cleaned);
  if (!Number.isFinite(parsed)) return null;

  return isNegative ? -parsed : parsed;
}

function formatNumber(options: FormatNumberOptions): string;
function formatNumber(
  value: NumericValue,
  minPrecision: number,
  maxPrecision?: number,
  style?: 'currency' | 'decimal',
  includeCommas?: boolean,
  fallback?: string,
  unit?: string
): string;
function formatNumber(
  valueOrOptions: NumericValue | FormatNumberOptions,
  minPrecision?: number,
  maxPrecision?: number,
  style: 'currency' | 'decimal' = 'decimal',
  includeCommas: boolean = true,
  fallback = '--',
  unit: string = ''
): string {
  // Handle object overload
  if (isPlainObject(valueOrOptions)) {
    const opts = valueOrOptions as FormatNumberOptions;
    return formatNumber(
      opts.value,
      opts.minPrecision,
      opts.maxPrecision,
      opts.style ?? 'decimal',
      opts.includeCommas ?? true,
      opts.fallback ?? '--',
      opts.unit ?? ''
    );
  }

  const rawValue = valueOrOptions as NumericValue;
  const value = parseNumericValue(rawValue);
  const formatter = new Intl.NumberFormat('en-US', {
    style,
    currency: 'USD',
    minimumFractionDigits: minPrecision!,
    maximumFractionDigits: maxPrecision ?? minPrecision!,
    useGrouping: includeCommas,
  });
  return !isNil(value) && Number.isFinite(value)
    ? `${formatter.format(value)}${unit ? ` ${unit}` : ''}`
    : fallback;
}

// Formats a count with percentage: "100 (26.24%)"
const formatCountWithPercent = (count: number, total: number): string => {
  if (total === 0) return formatNumber(count, 0, 0, 'decimal', true);
  const percent = (count / total) * 100;
  return `${formatNumber(count, 0, 0, 'decimal', true)} (${formatNumber(
    percent,
    2,
    2,
    'decimal',
    true
  )}%)`;
};

interface FormatRangeOptions {
  start: NumericValue;
  end: NumericValue;
  minPrecision?: number;
  maxPrecision?: number;
  includeCommas?: boolean;
  separator?: string;
  fallback?: string;
}

// Formats a numeric range: "1,000 - 2,000"
const formatRange = ({
  start,
  end,
  minPrecision = 0,
  maxPrecision,
  includeCommas = true,
  separator = ' - ',
  fallback = '--',
}: FormatRangeOptions): string => {
  const formattedStart = formatNumber({
    value: start,
    minPrecision,
    maxPrecision,
    includeCommas,
    fallback: '',
  });
  const formattedEnd = formatNumber({
    value: end,
    minPrecision,
    maxPrecision,
    includeCommas,
    fallback: '',
  });
  return (
    [formattedStart, formattedEnd].filter(Boolean).join(separator) || fallback
  );
};

// Fixed precision helper - formats with equal min/max precision and commas
const formatFixed = (
  value: NumericValue,
  precision: number,
  fallback = '--'
): string =>
  formatNumber({
    value,
    minPrecision: precision,
    maxPrecision: precision,
    includeCommas: true,
    fallback,
  });

// Factory for creating precision formatters
const withPrecision =
  (precision: number) =>
  (value: NumericValue, fallback = '--'): string =>
    formatFixed(value, precision, fallback);

// Common presets
const format0 = withPrecision(0);
const format1 = withPrecision(1);
const format2 = withPrecision(2);
const format3 = withPrecision(3);
const format8 = withPrecision(8);
const format10 = withPrecision(10);

// Formats a value ALREADY ON A 0-100 SCALE as a percentage string: "4.6%", "99.99%".
//
// THIS IS THE ONE THE OTHER TWO ARE NOT. formatCoveragePercent and formatConfidence below
// both take a FRACTION and multiply by 100, so neither can render a node the registry
// declares `percent` (0-100) — feeding either one a 4.6 renders "460%". INS-12069's unit
// work made that gap concrete: 24 registry nodes are declared `percent` and 7 `fraction`,
// and the two are indistinguishable in the payload, so the scaling has to come from the
// declared unit rather than from the number's magnitude.
//
// Trailing zeros are trimmed (0 to `maxPrecision` decimals) so a whole 25 reads "25%" rather
// than "25.00%" while 4.6 keeps its digit. Nullish and non-finite render the fallback.
//
// `maxPrecision` defaults to 4 — the on-screen precision every existing caller wants — but is
// overridable so a caller that must not truncate (the tearsheet Excel export, where a rounded
// fraction propagates into a reader's own maths) can keep more digits.
const formatPercentScale = (
  value: NumericValue | null | undefined,
  fallback = '—',
  maxPrecision = 4
): string => {
  if (value === null || value === undefined) return fallback;
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return `${formatNumber({
    value: parsed,
    minPrecision: 0,
    maxPrecision,
    includeCommas: true,
  })}%`;
};

// Formats a ratio (0–1) as a percentage string: "12.34%"
// Uses 2–10 decimal precision to preserve significant digits.
const formatCoveragePercent = (ratio: number): string =>
  `${formatNumber({
    value: ratio * 100,
    minPrecision: 2,
    maxPrecision: 10,
    includeCommas: true,
  })}%`;

// Formats a confidence ratio (0–1) as a whole-percent string: "85%".
// Rounds to the nearest integer (matching the AFE well match-review tiles,
// breakdown table, and agent-details accordion). Nullish values render the
// `fallback` (an em dash by default) so callers don't each re-implement the
// null guard. Note this is distinct from `formatCoveragePercent`, which keeps
// 2–10 decimals and uses Intl grouping.
const formatConfidence = (
  value: number | null | undefined,
  fallback = '—'
): string => {
  if (value === null || value === undefined) return fallback;
  return `${Math.round(value * 100)}%`;
};

// Formats a byte count as a human-readable size string with B/KB/MB/GB units,
// using one decimal place for KB and larger (e.g. "512 B", "1.5 KB", "2.3 MB").
// Returns `fallback` for nullish / non-finite input. Lives here (the shared
// number-format home) rather than as yet another feature-local copy; the two
// pre-existing feature-local variants (afe-catalog, insights-lexicon) can be
// migrated to this later without touching this file.
const formatBytes = (
  bytes: number | null | undefined,
  fallback = '--'
): string => {
  if (isNil(bytes) || !Number.isFinite(bytes) || bytes < 0) return fallback;
  // format0 applies the thousands separator so the 1000–1023 B window renders
  // "1,023 B" (not "1023 B"), consistent with the always-comma rule.
  if (bytes < 1024) return `${format0(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${format1(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${format1(mb)} MB`;
  return `${format1(mb / 1024)} GB`;
};

// Whole-dollar currency formatter used by per-row pricing columns (Eval 3.0).
// Renders `$X,XXX,XXX` for positives, `-$1,234`
// for negatives, `$0` for an explicit zero (zero is real for uneconomic
// slots), and `'--'` for nullish.
const formatWholeDollarCurrency = (
  value: number | null | undefined,
  fallback = '--'
): string =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    style: 'currency',
    includeCommas: true,
    fallback,
  });

// Two-decimal currency formatter — the cents-preserving sibling of
// `formatWholeDollarCurrency`. Renders `$X,XXX.XX` for positives, `-$1,234.56`
// for negatives, `$0.00` for an explicit zero, and `'--'` for nullish. Used by
// the AFE cost section, where the backend serves 2-decimal floats that must not
// be rounded off in display. Kept separate from `formatWholeDollarCurrency` so
// whole-dollar callers (e.g. Royalty Checks summary cards) are unaffected.
const formatDecimalCurrency = (
  value: number | null | undefined,
  fallback = '--'
): string =>
  formatNumber({
    value,
    minPrecision: 2,
    maxPrecision: 2,
    style: 'currency',
    includeCommas: true,
    fallback,
  });

/**
 * A signed percent string like "+30.5%" / "-4.0%" from an already-computed percentage — the
 * single source of the "+ sign, one decimal, % suffix" idiom shared by the comparison
 * `numericDelta` and comparison-totals' `formatDelta`. `format1` carries the minus for negatives.
 * @param {number} pct - The percentage value
 * @return {string} The signed, one-decimal percent string
 */
const signedPercent = (pct: number): string =>
  `${pct >= 0 ? '+' : ''}${format1(pct)}%`;

/**
 * A count as the banners print it — thousands-separated, no decimals — so a count
 * reads the same in every composed notice.
 *
 * @param {number} value - The count.
 * @return {string} The formatted count, e.g. "1,024".
 */
const countText = (value: number): string =>
  formatNumber({ value, minPrecision: 0, includeCommas: true });

export {
  countText,
  signedPercent,
  formatNumber,
  formatCountWithPercent,
  formatCoveragePercent,
  formatConfidence,
  formatPercentScale,
  formatRange,
  formatFixed,
  formatBytes,
  formatWholeDollarCurrency,
  formatDecimalCurrency,
  withPrecision,
  format0,
  format1,
  format2,
  format3,
  format8,
  format10,
  parseNumericValue,
  FormatNumberOptions,
  FormatRangeOptions,
  NumericValue,
};
